/**
 * Card Quantity Stepper
 *
 * A lightweight web component that adds +/- quantity controls to the
 * inline Add-to-Cart button on product cards. On desktop the stepper
 * is revealed on hover; on mobile it is revealed on first tap.
 *
 * Markup contract (see blocks/price.liquid):
 *   <card-quantity-stepper>
 *     <div class="card-qty-stepper">
 *       <button ref="minus">…</button>
 *       <span ref="display">1</span>
 *       <button ref="plus">…</button>
 *       <button ref="submit" type="submit">…</button>
 *     </div>
 *     <!-- original ATC button lives as a sibling -->
 *   </card-quantity-stepper>
 */

class CardQuantityStepper extends HTMLElement {
  #quantity = 1;
  #min = 1;
  #max = null;
  #step = 1;
  #expanded = false;

  connectedCallback() {
    // Read constraints from the hidden quantity input in the parent form
    const form = this.closest('form');
    const hiddenInput = form?.querySelector('input[name="quantity"]');
    if (hiddenInput) {
      this.#min = parseInt(hiddenInput.min) || parseInt(hiddenInput.dataset?.min) || 1;
      this.#max = hiddenInput.max ? parseInt(hiddenInput.max) : null;
      this.#step = parseInt(hiddenInput.step) || 1;
      this.#quantity = parseInt(hiddenInput.value) || this.#min;
    }

    // Button handlers
    this.querySelector('[data-action="minus"]')?.addEventListener('click', this.#handleMinus);
    this.querySelector('[data-action="plus"]')?.addEventListener('click', this.#handlePlus);
    this.querySelector('[data-action="submit"]')?.addEventListener('click', this.#handleSubmit);

    // Mobile: first tap on the component expands it
    this.addEventListener('click', this.#handleExpand);

    // Collapse when pointer leaves (desktop) or tap outside (mobile)
    this.addEventListener('mouseleave', this.#collapse);
    document.addEventListener('click', this.#handleOutsideClick);
  }

  disconnectedCallback() {
    this.querySelector('[data-action="minus"]')?.removeEventListener('click', this.#handleMinus);
    this.querySelector('[data-action="plus"]')?.removeEventListener('click', this.#handlePlus);
    this.querySelector('[data-action="submit"]')?.removeEventListener('click', this.#handleSubmit);
    this.removeEventListener('click', this.#handleExpand);
    this.removeEventListener('mouseleave', this.#collapse);
    document.removeEventListener('click', this.#handleOutsideClick);
  }

  #handleMinus = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const newVal = this.#quantity - this.#step;
    if (newVal >= this.#min) {
      this.#quantity = newVal;
      this.#sync();
    }
  };

  #handlePlus = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const newVal = this.#quantity + this.#step;
    if (this.#max === null || newVal <= this.#max) {
      this.#quantity = newVal;
      this.#sync();
    }
  };

  #handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Update the hidden input and submit the form
    this.#syncHiddenInput();
    const form = this.closest('form');
    if (form) {
      // Use requestSubmit so the product-form-component's on:submit handler fires
      form.requestSubmit();
    }
  };

  #handleExpand = (e) => {
    // On touch devices, first tap expands; stepper buttons handle their own clicks
    if (!this.#expanded) {
      // Don't prevent default on desktop — hover handles expansion via CSS
      if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
        e.preventDefault();
        e.stopPropagation();
        this.#expand();
      }
    }
  };

  #handleOutsideClick = (e) => {
    if (this.#expanded && !this.contains(e.target)) {
      this.#collapse();
    }
  };

  #expand = () => {
    this.#expanded = true;
    this.setAttribute('data-expanded', '');
  };

  #collapse = () => {
    this.#expanded = false;
    this.removeAttribute('data-expanded');
    // Reset quantity to min when collapsing
    this.#quantity = this.#min;
    this.#sync();
  };

  /** Update the visible display and the hidden form input */
  #sync() {
    const display = this.querySelector('[data-qty-display]');
    if (display) display.textContent = this.#quantity;

    const minus = this.querySelector('[data-action="minus"]');
    if (minus) minus.toggleAttribute('disabled', this.#quantity <= this.#min);

    const plus = this.querySelector('[data-action="plus"]');
    if (plus) plus.toggleAttribute('disabled', this.#max !== null && this.#quantity >= this.#max);

    this.#syncHiddenInput();
  }

  #syncHiddenInput() {
    const form = this.closest('form');
    const hiddenInput = form?.querySelector('input[name="quantity"]');
    if (hiddenInput) hiddenInput.value = this.#quantity;
  }
}

if (!customElements.get('card-quantity-stepper')) {
  customElements.define('card-quantity-stepper', CardQuantityStepper);
}
